import React, { useState, useEffect, useCallback } from 'react';
import {
  Lock,
  Loader2,
  Trash2,
  Edit3,
  Save,
  X,
  Clock,
  CheckCircle2,
  AlertCircle,
  StickyNote,
  MessageSquare,
} from 'lucide-react';
import axios from 'axios';
import { toast } from '../hooks/use-toast';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusOptions = ['Waiting', 'In Progress', 'Completed'];
const typeOptions = [
  'VRChat PC Retexture - $30',
  'VRChat Quest Retexture - $30',
  'PC + Quest Bundle - $45',
  'VRM/VTuber Avatar - $35',
];

const statusColors = {
  'In Progress': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/40',
  Waiting: 'bg-slate-700/40 text-gray-400 border-slate-600',
  Completed: 'bg-green-500/20 text-green-400 border-green-500/40',
};

const Admin = () => {
  const [pin, setPin] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [commissionsOpen, setCommissionsOpen] = useState(true);
  const [togglingStatus, setTogglingStatus] = useState(false);
  const [msgInProgress, setMsgInProgress] = useState('');
  const [msgCompleted, setMsgCompleted] = useState('');
  const [savingMessages, setSavingMessages] = useState(false);

  const storedPin = authenticated ? pin : '';

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const adminPin = localStorage.getItem('sniderwolf_admin_pin') || '';
      const res = await axios.get(`${API}/admin/queue`, {
        headers: { 'X-Admin-Pin': adminPin },
      });
      setQueue(res.data);
    } catch (err) {
      console.error('Failed to fetch queue:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setCommissionsOpen(res.data.commissions_open);
      setMsgInProgress(res.data.msg_in_progress || '');
      setMsgCompleted(res.data.msg_completed || '');
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    }
  }, []);

  const saveMessages = async () => {
    setSavingMessages(true);
    try {
      await axios.put(
        `${API}/settings/messages`,
        { msg_in_progress: msgInProgress, msg_completed: msgCompleted },
        { headers: { 'X-Admin-Pin': storedPin } }
      );
      toast({ title: 'Messages Saved', description: 'Discord notification wording updated.' });
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to save messages.', variant: 'destructive' });
    } finally {
      setSavingMessages(false);
    }
  };

  useEffect(() => {
    const saved = localStorage.getItem('sniderwolf_admin_pin');
    if (saved) {
      setPin(saved);
      setAuthenticated(true);
      fetchQueue();
      fetchSettings();
    }
  }, [fetchQueue, fetchSettings]);

  const toggleCommissions = async () => {    setTogglingStatus(true);
    try {
      const res = await axios.put(
        `${API}/settings`,
        { commissions_open: !commissionsOpen },
        { headers: { 'X-Admin-Pin': storedPin } }
      );
      setCommissionsOpen(res.data.commissions_open);
      toast({
        title: res.data.commissions_open ? 'Commissions Opened' : 'Commissions Closed',
        description: `Commission status changed to ${res.data.commissions_open ? 'Open' : 'Closed'}.`,
      });
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to update status.', variant: 'destructive' });
    } finally {
      setTogglingStatus(false);
    }
  };

  const handleVerifyPin = async (e) => {
    e.preventDefault();
    setVerifying(true);
    try {
      await axios.post(`${API}/admin/verify-pin`, { pin });
      setAuthenticated(true);
      localStorage.setItem('sniderwolf_admin_pin', pin);
      fetchQueue();
      fetchSettings();
      toast({ title: 'Access Granted', description: 'Welcome, admin!' });
    } catch (err) {
      toast({
        title: 'Access Denied',
        description: 'Invalid PIN code.',
        variant: 'destructive',
      });
    } finally {
      setVerifying(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this commission?')) return;
    try {
      await axios.delete(`${API}/queue/${id}`, {
        headers: { 'X-Admin-Pin': storedPin },
      });
      setQueue(queue.filter((item) => item.id !== id));
      toast({ title: 'Deleted', description: 'Commission removed from queue.' });
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to delete.',
        variant: 'destructive',
      });
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setEditData({
      client: item.client,
      contact_method: item.contact_method,
      commission_type: item.commission_type,
      status: item.status,
      description: item.description,
      admin_notes: item.admin_notes || '',
    });
  };

  const handleSave = async (id) => {
    try {
      const res = await axios.put(`${API}/queue/${id}`, editData, {
        headers: { 'X-Admin-Pin': storedPin },
      });
      setQueue(queue.map((item) => (item.id === id ? res.data : item)));
      setEditingId(null);
      toast({ title: 'Updated', description: 'Commission updated successfully.' });
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to update.',
        variant: 'destructive',
      });
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const res = await axios.put(
        `${API}/queue/${id}`,
        { status: newStatus },
        { headers: { 'X-Admin-Pin': storedPin } }
      );
      setQueue(queue.map((item) => (item.id === id ? res.data : item)));
      toast({ title: 'Status Updated', description: `Changed to ${newStatus}` });
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to update status.',
        variant: 'destructive',
      });
    }
  };

  // PIN entry screen
  if (!authenticated) {
    return (
      <div className="pt-32 pb-20 px-4">
        <div className="max-w-md mx-auto text-center">
          <Lock className="w-20 h-20 text-cyan-400 mx-auto mb-6" />
          <h1 className="text-4xl font-bold text-white mb-4">Admin Access</h1>
          <p className="text-gray-300 mb-8">Enter your PIN to manage the queue</p>

          <form onSubmit={handleVerifyPin} className="space-y-4">
            <input
              type="password"
              className="w-full h-14 rounded-xl border px-4 text-center text-2xl tracking-[0.5em] font-bold shadow-sm bg-slate-800/50 border-slate-600 text-white placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-cyan-400"
              placeholder="• • • •"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              maxLength={10}
              autoFocus
            />
            <button
              className="inline-flex items-center justify-center gap-2 text-sm font-medium rounded-md px-8 w-full py-3 bg-gradient-to-r from-teal-600 via-cyan-600 to-teal-600 hover:from-teal-500 hover:via-cyan-500 hover:to-teal-500 text-white shadow-lg shadow-teal-500/50 hover:shadow-xl transition-all duration-300 hover:scale-105 disabled:opacity-50"
              type="submit"
              disabled={verifying || !pin}
            >
              {verifying ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Lock className="w-5 h-5" />
              )}
              {verifying ? 'Verifying...' : 'Unlock'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Admin panel
  return (
    <div className="pt-32 pb-20 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-cyan-300 via-teal-400 to-cyan-400 bg-clip-text text-transparent mb-2">
            Admin Panel
          </h1>
          <p className="text-gray-300">
            Manage commission queue • {queue.length} item{queue.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Commission Status Toggle */}
        <div className="mb-8">
          <div className={`rounded-xl border backdrop-blur p-5 flex items-center justify-between ${
            commissionsOpen
              ? 'bg-green-500/10 border-green-500/30'
              : 'bg-red-500/10 border-red-500/30'
          }`}>
            <div>
              <h3 className="text-white font-semibold text-lg">Commission Status</h3>
              <p className={`text-sm font-medium ${commissionsOpen ? 'text-green-400' : 'text-red-400'}`}>
                Currently: {commissionsOpen ? 'Open' : 'Closed'}
              </p>
            </div>
            <button
              onClick={toggleCommissions}
              disabled={togglingStatus}
              className={`inline-flex items-center gap-2 px-6 py-2.5 rounded-md text-sm font-medium transition-all duration-300 hover:scale-105 disabled:opacity-50 ${
                commissionsOpen
                  ? 'bg-red-500/20 border border-red-500/40 text-red-400 hover:bg-red-500/30'
                  : 'bg-green-500/20 border border-green-500/40 text-green-400 hover:bg-green-500/30'
              }`}
            >
              {togglingStatus ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : commissionsOpen ? (
                'Close Commissions'
              ) : (
                'Open Commissions'
              )}
            </button>
          </div>
        </div>

        {/* Discord Message Templates */}
        <div className="mb-8">
          <div className="rounded-xl border bg-slate-800/40 border-slate-700 backdrop-blur p-5" data-testid="discord-messages-editor">
            <div className="flex items-center gap-2 mb-1">
              <MessageSquare className="w-5 h-5 text-cyan-400" />
              <h3 className="text-white font-semibold text-lg">Discord Notification Messages</h3>
            </div>
            <p className="text-gray-400 text-sm mb-4">
              Edit the wording posted to Discord when you change a status. Use{' '}
              <code className="text-cyan-300">{'{client}'}</code>,{' '}
              <code className="text-cyan-300">{'{type}'}</code> and{' '}
              <code className="text-cyan-300">{'{position}'}</code> as placeholders.
            </p>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-amber-400 block mb-1">"In Progress" message</label>
                <input
                  data-testid="msg-in-progress-input"
                  className="w-full h-10 rounded-md border px-3 text-sm bg-slate-800/50 border-slate-600 text-white focus:outline-none focus:ring-1 focus:ring-cyan-400"
                  value={msgInProgress}
                  onChange={(e) => setMsgInProgress(e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs text-green-400 block mb-1">"Completed" message</label>
                <input
                  data-testid="msg-completed-input"
                  className="w-full h-10 rounded-md border px-3 text-sm bg-slate-800/50 border-slate-600 text-white focus:outline-none focus:ring-1 focus:ring-cyan-400"
                  value={msgCompleted}
                  onChange={(e) => setMsgCompleted(e.target.value)}
                />
              </div>
              <div className="flex justify-end">
                <button
                  data-testid="save-messages-btn"
                  onClick={saveMessages}
                  disabled={savingMessages}
                  className="inline-flex items-center gap-1.5 px-5 py-2 rounded-md text-sm font-medium text-white bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-500 hover:to-cyan-500 transition-all disabled:opacity-50"
                >
                  {savingMessages ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save Messages
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          {[
            { label: 'Waiting', count: queue.filter((q) => q.status === 'Waiting').length, color: 'text-gray-400' },
            { label: 'In Progress', count: queue.filter((q) => q.status === 'In Progress').length, color: 'text-cyan-400' },
            { label: 'Completed', count: queue.filter((q) => q.status === 'Completed').length, color: 'text-green-400' },
          ].map((stat) => (
            <div
              key={stat.label}
              className="rounded-xl border bg-slate-800/40 border-slate-700 p-4 text-center backdrop-blur"
            >
              <p className={`text-3xl font-bold ${stat.color}`}>{stat.count}</p>
              <p className="text-sm text-gray-400">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Loading */}
        {loading && (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 text-cyan-400 animate-spin mx-auto" />
          </div>
        )}

        {/* Empty */}
        {!loading && queue.length === 0 && (
          <div className="text-center text-gray-400 py-16">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-xl">Queue is empty</p>
            <p className="text-sm mt-1">Commission requests will appear here.</p>
          </div>
        )}

        {/* Queue items */}
        {!loading && queue.length > 0 && (
          <div className="space-y-4">
            {queue.map((item) => (
              <div
                key={item.id}
                className="rounded-xl border bg-slate-800/40 border-slate-700 backdrop-blur overflow-hidden"
              >
                {editingId === item.id ? (
                  /* Editing mode */
                  <div className="p-5 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-gray-400 block mb-1">Client Name</label>
                        <input
                          className="w-full h-9 rounded-md border px-3 text-sm bg-slate-800/50 border-slate-600 text-white focus:outline-none focus:ring-1 focus:ring-cyan-400"
                          value={editData.client}
                          onChange={(e) => setEditData({ ...editData, client: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-400 block mb-1">Contact Method</label>
                        <input
                          className="w-full h-9 rounded-md border px-3 text-sm bg-slate-800/50 border-slate-600 text-white focus:outline-none focus:ring-1 focus:ring-cyan-400"
                          value={editData.contact_method}
                          onChange={(e) =>
                            setEditData({ ...editData, contact_method: e.target.value })
                          }
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-400 block mb-1">Commission Type</label>
                        <Select
                          value={editData.commission_type}
                          onValueChange={(val) =>
                            setEditData({ ...editData, commission_type: val })
                          }
                        >
                          <SelectTrigger className="w-full bg-slate-800/50 border-slate-600 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {typeOptions.map((t) => (
                              <SelectItem key={t} value={t}>
                                {t}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs text-gray-400 block mb-1">Status</label>
                        <Select
                          value={editData.status}
                          onValueChange={(val) => setEditData({ ...editData, status: val })}
                        >
                          <SelectTrigger className="w-full bg-slate-800/50 border-slate-600 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {statusOptions.map((s) => (
                              <SelectItem key={s} value={s}>
                                {s}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-400 block mb-1">Description</label>
                      <textarea
                        className="w-full min-h-[60px] rounded-md border px-3 py-2 text-sm bg-slate-800/50 border-slate-600 text-white focus:outline-none focus:ring-1 focus:ring-cyan-400"
                        rows={3}
                        value={editData.description}
                        onChange={(e) =>
                          setEditData({ ...editData, description: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <label className="text-xs text-amber-400/90 block mb-1 flex items-center gap-1.5">
                        <StickyNote className="w-3.5 h-3.5" /> Private Notes (only you can see these)
                      </label>
                      <textarea
                        data-testid="admin-notes-input"
                        className="w-full min-h-[60px] rounded-md border px-3 py-2 text-sm bg-amber-500/5 border-amber-500/30 text-amber-100 placeholder:text-amber-200/30 focus:outline-none focus:ring-1 focus:ring-amber-400"
                        rows={2}
                        placeholder="Add private tracking notes for this request..."
                        value={editData.admin_notes}
                        onChange={(e) =>
                          setEditData({ ...editData, admin_notes: e.target.value })
                        }
                      />
                    </div>
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => setEditingId(null)}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium text-gray-300 bg-slate-700 hover:bg-slate-600 transition-colors"
                      >
                        <X className="w-4 h-4" /> Cancel
                      </button>
                      <button
                        onClick={() => handleSave(item.id)}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium text-white bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-500 hover:to-cyan-500 transition-all"
                      >
                        <Save className="w-4 h-4" /> Save
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Display mode */
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-center gap-4 flex-1">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0 ${
                            item.status === 'In Progress'
                              ? 'bg-cyan-500/20 text-cyan-400'
                              : item.status === 'Completed'
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-slate-700/60 text-gray-400'
                          }`}
                        >
                          {item.position}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-1">
                            <h3 className="text-white font-semibold">{item.client}</h3>
                            <span
                              className={`text-xs font-medium px-2 py-0.5 rounded-full border ${
                                statusColors[item.status] || statusColors['Waiting']
                              }`}
                            >
                              {item.status}
                            </span>
                          </div>
                          <p className="text-gray-400 text-sm">{item.commission_type}</p>
                          {item.description && (
                            <p className="text-gray-500 text-xs mt-1 truncate max-w-xl">
                              {item.description}
                            </p>
                          )}
                          {item.contact_method && (
                            <p className="text-gray-500 text-xs mt-0.5">
                              Contact: {item.contact_method}
                            </p>
                          )}
                          {item.paypal && (
                            <p className="text-gray-500 text-xs">
                              PayPal: {item.paypal}
                            </p>
                          )}
                          {item.admin_notes && (
                            <div
                              data-testid={`admin-note-${item.id}`}
                              className="mt-2 rounded-md bg-amber-500/10 border border-amber-500/30 px-3 py-2"
                            >
                              <p className="text-amber-400/90 text-xs font-medium flex items-center gap-1.5 mb-0.5">
                                <StickyNote className="w-3.5 h-3.5" /> Private Note
                              </p>
                              <p className="text-amber-100/90 text-xs whitespace-pre-wrap">{item.admin_notes}</p>
                            </div>
                          )}
                          {item.reference_images && item.reference_images.length > 0 && (
                            <div className="mt-2" data-testid={`ref-images-${item.id}`}>
                              <p className="text-gray-400 text-xs mb-1">
                                Reference Images ({item.reference_images.length}):
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {item.reference_images.map((imgId) => (
                                  <a
                                    key={imgId}
                                    href={`${API}/commissions/image/${imgId}?pin=${storedPin}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`ref-image-${imgId}`}
                                  >
                                    <img
                                      src={`${API}/commissions/image/${imgId}?pin=${storedPin}`}
                                      alt="reference"
                                      className="w-16 h-16 object-cover rounded-md border border-slate-600 hover:border-cyan-400 transition-colors"
                                    />
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 flex-shrink-0">
                        {/* Quick status buttons */}
                        {item.status !== 'In Progress' && (
                          <button
                            onClick={() => handleStatusChange(item.id, 'In Progress')}
                            className="p-2 rounded-lg bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/20 transition-colors"
                            title="Set In Progress"
                          >
                            <Clock className="w-4 h-4" />
                          </button>
                        )}
                        {item.status !== 'Completed' && (
                          <button
                            onClick={() => handleStatusChange(item.id, 'Completed')}
                            className="p-2 rounded-lg bg-green-500/10 border border-green-500/30 text-green-400 hover:bg-green-500/20 transition-colors"
                            title="Set Completed"
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleEdit(item)}
                          className="p-2 rounded-lg bg-slate-700/50 border border-slate-600 text-gray-300 hover:bg-slate-600 transition-colors"
                          title="Edit"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="p-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
